import { Lock } from 'lucide-react';
import {
  AGENT_PARENT_REPLIES_ENABLED,
  MessageVisibility,
  isTicketListSocketPayload,
  type TicketUpdatedSocketPayload,
  type TicketSocketUpdate,
} from '@21k/shared';
import { FormEvent, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import {
  createComment,
  getApiErrorMessage,
  getAssetUrl,
  listComments,
  searchMentionUsers,
  uploadAssets,
} from '../api/client';
import type { Asset, Comment } from '../api/types';
import { ATTACHMENT_ONLY_COMMENT } from '../lib/assetUtils';
import { useAuth } from '../contexts/AuthContext';
import { useSocketReconnect } from '../contexts/SocketContext';
import { useTicketSocket } from '../hooks/useTicketSocket';
import { canReplyToParentOnAnyTicket } from '../utils/permissions';
import AttachmentPicker from './AttachmentPicker';
import { AssetPreview } from './AssetPreview';
import { upsertComment } from '../utils/commentMerge';
import { socketCommentToComment } from '../utils/socketComment';
import { renderMentionContent } from '../utils/ticketDisplay';

type ComposerMode = 'parent' | 'internal';

function formatMessageTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDateLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  });
}

function sameDay(a: string, b: string): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

function authorInitials(comment: Comment): string {
  const name = messageAuthorName(comment);
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
  return `${parts[0]!.charAt(0)}${parts[parts.length - 1]!.charAt(0)}`.toUpperCase();
}

type Props = {
  ticketId: string;
  onRefresh: () => void;
  onTicketUpdate?: (update: TicketSocketUpdate) => void;
  /** Active assignee — when set and matches current user, parent reply tab is shown */
  activeAssigneeId?: string | null;
  /** Ticket detail page: scroll body + pinned composer */
  layout?: 'default' | 'ticket-detail';
  scrollPrefix?: ReactNode;
};

function messageAuthorName(comment: Comment): string {
  if (comment.authorType === 'SYSTEM') return '';
  if (comment.author) {
    return `${comment.author.firstName} ${comment.author.lastName}`.trim();
  }
  return comment.parentAuthorName ?? 'Parent';
}

function shouldShowMessageBody(comment: Comment): boolean {
  if (!comment.content.trim()) return false;
  if (comment.content === ATTACHMENT_ONLY_COMMENT && comment.assets.length > 0) return false;
  return true;
}

function MessageAssets({ assets }: { assets: Asset[] }) {
  if (assets.length === 0) return null;

  return (
    <div className="conv-assets conv-assets-preview">
      {assets.map((asset) => (
        <AssetPreview
          key={asset.id}
          asset={{
            id: asset.id,
            url: asset.url ?? '',
            originalName: asset.fileName,
            mimeType: asset.mimeType,
            size: asset.sizeBytes,
          }}
          resolveUrl={() =>
            asset.url
              ? Promise.resolve(asset.url)
              : getAssetUrl(asset.id, asset.ticketId).then((r) => r.url)
          }
        />
      ))}
    </div>
  );
}

function formatWhatsAppDateLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  if (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  ) {
    return 'TODAY';
  }
  return d
    .toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
    .toUpperCase();
}

function MessageBubble({
  comment,
  showAuthorLabel,
  whatsappStyle = false,
}: {
  comment: Comment;
  showAuthorLabel: boolean;
  whatsappStyle?: boolean;
}) {
  if (comment.isSystemEvent || comment.visibility === MessageVisibility.SYSTEM_EVENT) {
    if (whatsappStyle) {
      return (
        <div className="conv-msg-row conv-msg-row-system">
          <span className="conv-wa-system-chip">
            {comment.content}
            <time className="conv-wa-system-time">{formatMessageTime(comment.createdAt)}</time>
          </span>
        </div>
      );
    }
    return (
      <div className="conv-system-event conv-system-separator">
        <span className="conv-system-line" />
        <span className="conv-system-text">{comment.content}</span>
        <span className="conv-system-line" />
        <time className="conv-system-time">{formatMessageTime(comment.createdAt)}</time>
      </div>
    );
  }

  const isInternal = comment.visibility === MessageVisibility.INTERNAL_NOTE;
  const isParent = comment.authorType === 'PARENT';
  const isAgentToParent =
    comment.visibility === MessageVisibility.CUSTOMER_VISIBLE && comment.authorType === 'AGENT';

  const authorName = messageAuthorName(comment);

  if (whatsappStyle) {
    if (isInternal) {
      return (
        <div className="conv-msg-row conv-msg-row-internal">
          <article className="conv-msg conv-msg-internal conv-wa-internal">
            <div className="conv-internal-label">Internal</div>
            {shouldShowMessageBody(comment) && (
              <div className="conv-body">{renderMentionContent(comment.content)}</div>
            )}
            <MessageAssets assets={comment.assets} />
            <div className="conv-wa-internal-footer">
              {authorName && <span>{authorName}</span>}
              <time>{formatMessageTime(comment.createdAt)}</time>
            </div>
          </article>
        </div>
      );
    }

    if (isParent) {
      return (
        <div className="conv-msg-row conv-msg-row-parent">
          <article className="conv-msg conv-msg-parent conv-wa-bubble conv-wa-bubble-parent">
            <div className="conv-body">
              {shouldShowMessageBody(comment) && renderMentionContent(comment.content)}
              <time className="conv-wa-time">{formatMessageTime(comment.createdAt)}</time>
            </div>
            <MessageAssets assets={comment.assets} />
          </article>
        </div>
      );
    }

    if (isAgentToParent) {
      return (
        <div className="conv-msg-row conv-msg-row-agent-reply">
          <div className="conv-msg-stack">
            {showAuthorLabel && authorName && (
              <span className="conv-wa-agent-name">{authorName}</span>
            )}
            <article className="conv-msg conv-msg-agent-out conv-wa-bubble conv-wa-bubble-agent">
              <div className="conv-body">
                {shouldShowMessageBody(comment) && renderMentionContent(comment.content)}
                <time className="conv-wa-time">{formatMessageTime(comment.createdAt)}</time>
              </div>
              {comment.sentViaChannel && (
                <div className="conv-sent-label">Sent to parent</div>
              )}
              <MessageAssets assets={comment.assets} />
            </article>
          </div>
        </div>
      );
    }

    return (
      <div className="conv-msg-row conv-msg-row-agent-reply">
        <div className="conv-msg-stack">
          {authorName && <span className="conv-wa-agent-name">{authorName}</span>}
          <article className="conv-msg conv-msg-agent conv-wa-bubble conv-wa-bubble-agent">
            <div className="conv-body">
              {shouldShowMessageBody(comment) && renderMentionContent(comment.content)}
              <time className="conv-wa-time">{formatMessageTime(comment.createdAt)}</time>
            </div>
            <MessageAssets assets={comment.assets} />
          </article>
        </div>
      </div>
    );
  }

  const bubbleClass = isInternal
    ? 'conv-msg conv-msg-internal'
    : isParent
      ? 'conv-msg conv-msg-parent'
      : isAgentToParent
        ? 'conv-msg conv-msg-agent-out'
        : 'conv-msg conv-msg-agent';

  const bubble = (
    <article className={bubbleClass}>
      {isInternal && (
        <div className="conv-internal-label">
          <Lock size={11} strokeWidth={2} aria-hidden />
          Internal
        </div>
      )}
      {showAuthorLabel && authorName && (
        <div className="conv-msg-head">
          <span className="conv-author">{authorName}</span>
          <time className="conv-time">{formatMessageTime(comment.createdAt)}</time>
        </div>
      )}
      {!showAuthorLabel && authorName && (
        <time className="conv-time conv-time-inline">{formatMessageTime(comment.createdAt)}</time>
      )}
      {shouldShowMessageBody(comment) && (
        <div className="conv-body">{renderMentionContent(comment.content)}</div>
      )}
      {comment.sentViaChannel && isAgentToParent && (
        <div className="conv-sent-label">Sent to parent</div>
      )}
      <MessageAssets assets={comment.assets} />
      {!authorName && (
        <time className="conv-time">{formatMessageTime(comment.createdAt)}</time>
      )}
    </article>
  );

  if (isParent || isAgentToParent) {
    return <div className={`conv-msg-row${isAgentToParent ? ' conv-msg-row-out' : ''}`}>{bubble}</div>;
  }

  if (!isInternal && !isParent) {
    return (
      <div className="conv-msg-row conv-msg-row-agent">
        <span className="conv-msg-avatar" aria-hidden>
          {authorInitials(comment)}
        </span>
        <div className="conv-msg-stack">{bubble}</div>
      </div>
    );
  }

  return <div className="conv-msg-row conv-msg-row-internal">{bubble}</div>;
}

export default function ConversationThread({
  ticketId,
  onRefresh,
  onTicketUpdate,
  activeAssigneeId,
  layout = 'default',
  scrollPrefix,
}: Props) {
  const { user } = useAuth();
  const isAssigned = Boolean(user && activeAssigneeId && activeAssigneeId === user.id);
  const hasAdminAccess = user ? canReplyToParentOnAnyTicket(user) : false;
  const canReplyToParent =
    AGENT_PARENT_REPLIES_ENABLED && (isAssigned || hasAdminAccess);
  const socket = useTicketSocket(ticketId);
  const [messages, setMessages] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [text, setText] = useState('');
  const [mode, setMode] = useState<ComposerMode>(() => (canReplyToParent ? 'parent' : 'internal'));
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionOptions, setMentionOptions] = useState<
    Array<{ id: string; firstName: string; lastName: string; email: string }>
  >([]);
  const [busy, setBusy] = useState(false);
  const [typingLabel, setTypingLabel] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<number | null>(null);
  const typingClearRef = useRef<number | null>(null);
  const typingEmitRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listComments(ticketId, { page: 1, pageSize: 200, sort: 'oldest' });
      setMessages(res.items);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useSocketReconnect(load);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!canReplyToParent) {
      setMode('internal');
    }
  }, [canReplyToParent]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  useEffect(() => {
    if (!socket) return;

    const onMessage = (comment: Parameters<typeof socketCommentToComment>[0]) => {
      const mapped = socketCommentToComment(comment);
      setMessages((prev) => upsertComment(prev, mapped));
    };

    const onTicketUpdated = (payload: TicketUpdatedSocketPayload) => {
      if (isTicketListSocketPayload(payload)) return;
      onTicketUpdate?.(payload);
    };

    const onTyping = (data: { userId: string; name: string; ticketId: string }) => {
      if (data.ticketId !== ticketId) return;
      if (user && data.userId === user.id) return;
      setTypingLabel(data.name ? `${data.name} is typing…` : 'Someone is typing…');
      if (typingClearRef.current) window.clearTimeout(typingClearRef.current);
      typingClearRef.current = window.setTimeout(() => setTypingLabel(''), 3000);
    };

    const onStoppedTyping = (data: { userId: string; ticketId: string }) => {
      if (data.ticketId !== ticketId) return;
      if (user && data.userId === user.id) return;
      setTypingLabel('');
    };

    socket.on('message:new', onMessage);
    socket.on('ticket:updated', onTicketUpdated);
    socket.on('user:typing', onTyping);
    socket.on('user:stopped_typing', onStoppedTyping);

    return () => {
      socket.off('message:new', onMessage);
      socket.off('ticket:updated', onTicketUpdated);
      socket.off('user:typing', onTyping);
      socket.off('user:stopped_typing', onStoppedTyping);
    };
  }, [socket, ticketId, onTicketUpdate, user]);

  useEffect(
    () => () => {
      if (typingTimeoutRef.current) window.clearTimeout(typingTimeoutRef.current);
      if (typingClearRef.current) window.clearTimeout(typingClearRef.current);
      if (typingEmitRef.current) window.clearTimeout(typingEmitRef.current);
    },
    [],
  );

  function emitTypingStop() {
    if (!socket || !ticketId) return;
    socket.emit('typing:stop', ticketId);
  }

  function activeMentionQuery(value: string): string | null {
    const match = value.match(/(?:^|\s)@([^\s@]*)$/);
    return match ? (match[1] ?? '') : null;
  }

  function insertMention(person: { id: string; firstName: string; lastName: string }) {
    const name = `${person.firstName} ${person.lastName}`.trim();
    const next = text.replace(/(?:^|\s)@([^\s@]*)$/, (full) => {
      const lead = full.startsWith('@') ? '' : full.charAt(0);
      return `${lead}@[${name}](${person.id}) `;
    });
    setText(next);
    setMentionQuery(null);
    setMentionOptions([]);
  }

  function handleComposerChange(value: string) {
    setText(value);
    const query = activeMentionQuery(value);
    setMentionQuery(query);
    if (query === null) setMentionOptions([]);
    if (!socket || !ticketId) return;

    if (typingEmitRef.current) window.clearTimeout(typingEmitRef.current);
    typingEmitRef.current = window.setTimeout(() => {
      socket.emit('typing:start', ticketId);
    }, 300);
  }

  useEffect(() => {
    if (mentionQuery === null || mentionQuery.length < 1) return;
    const timer = window.setTimeout(() => {
      void searchMentionUsers(mentionQuery)
        .then(setMentionOptions)
        .catch(() => setMentionOptions([]));
    }, 200);
    return () => window.clearTimeout(timer);
  }, [mentionQuery]);

  async function handleSend(e?: FormEvent) {
    e?.preventDefault();
    if (!text.trim() && pendingFiles.length === 0) return;

    emitTypingStop();
    setBusy(true);
    setError('');
    try {
      const effectiveMode = canReplyToParent ? mode : 'internal';
      const visibility =
        effectiveMode === 'parent'
          ? MessageVisibility.CUSTOMER_VISIBLE
          : MessageVisibility.INTERNAL_NOTE;

      const content =
        text.trim() || (pendingFiles.length > 0 ? ATTACHMENT_ONLY_COMMENT : '');

      const comment = await createComment(ticketId, content, { visibility });
      if (pendingFiles.length > 0) {
        await uploadAssets(ticketId, pendingFiles, comment.id);
      }

      setMessages((prev) => upsertComment(prev, comment));

      setText('');
      setPendingFiles([]);
      setMentionQuery(null);
      setMentionOptions([]);
      onRefresh();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const canSubmit = text.trim().length > 0 || pendingFiles.length > 0;
  const isTicketDetailLayout = layout === 'ticket-detail';

  const messagesEl = (
    <div className="conv-messages">
        {loading ? (
          <div className="empty-state">Loading conversation…</div>
        ) : messages.length === 0 ? (
          <div className="empty-state">No messages yet.</div>
        ) : (
          (() => {
            const nodes: React.ReactNode[] = [];
            let lastDate = '';
            let lastAgentKey = '';

            messages.forEach((m, index) => {
              const dateLabel = isTicketDetailLayout
                ? formatWhatsAppDateLabel(m.createdAt)
                : formatDateLabel(m.createdAt);
              const prev = index > 0 ? messages[index - 1] : undefined;
              if (
                dateLabel !== lastDate &&
                (!prev || !sameDay(prev.createdAt, m.createdAt))
              ) {
                lastDate = dateLabel;
                nodes.push(
                  isTicketDetailLayout ? (
                    <div key={`date-${m.id}`} className="conv-msg-row conv-msg-row-system">
                      <span className="conv-wa-date-chip">{dateLabel}</span>
                    </div>
                  ) : (
                    <div key={`date-${m.id}`} className="date-separator">
                      <span className="date-sep-line" />
                      <span className="date-sep-text">{dateLabel}</span>
                      <span className="date-sep-line" />
                    </div>
                  ),
                );
              }

              const agentKey =
                m.authorType === 'AGENT' && m.author
                  ? m.author.id
                  : m.authorType === 'PARENT'
                    ? 'parent'
                    : '';
              const showAuthorLabel =
                m.authorType === 'AGENT' &&
                agentKey.length > 0 &&
                agentKey !== lastAgentKey &&
                !m.isSystemEvent;

              if (showAuthorLabel) lastAgentKey = agentKey;
              else if (m.authorType === 'PARENT' || m.isSystemEvent) lastAgentKey = '';

              nodes.push(
                <MessageBubble
                  key={m.id}
                  comment={m}
                  showAuthorLabel={showAuthorLabel}
                  whatsappStyle={isTicketDetailLayout}
                />,
              );
            });

            return nodes;
          })()
        )}
        {typingLabel && <div className="conv-typing-indicator">{typingLabel}</div>}
        <div ref={bottomRef} />
      </div>
  );

  const composerEl = (
      <form
        className={`conv-composer ${mode === 'parent' && canReplyToParent ? 'conv-composer-parent' : 'conv-composer-internal'}${isTicketDetailLayout ? ' conv-composer--whatsapp' : ''}`}
        onSubmit={(e) => void handleSend(e)}
      >
        {AGENT_PARENT_REPLIES_ENABLED ? (
          canReplyToParent ? (
            <div className="conv-mode-toggle">
              <button
                type="button"
                className={`conv-mode-btn ${mode === 'parent' ? 'active' : ''}`}
                onClick={() => setMode('parent')}
              >
                Reply to parent
              </button>
              <button
                type="button"
                className={`conv-mode-btn ${mode === 'internal' ? 'active' : ''}`}
                onClick={() => setMode('internal')}
              >
                Internal note
              </button>
            </div>
          ) : (
            <div className="conv-composer-locked-note">
              Internal note only — ticket is not assigned to you
            </div>
          )
        ) : (
          <div className="conv-mode-toggle" aria-label="Message visibility">
            <span className="conv-mode-btn active conv-mode-btn-static">Internal note</span>
          </div>
        )}
        {pendingFiles.length > 0 && (
          <AttachmentPicker
            id="conv-attachments-chips"
            files={pendingFiles}
            onChange={setPendingFiles}
            disabled={busy}
            chipsOnly
          />
        )}
        <div className={isTicketDetailLayout ? 'conv-wa-composer-row' : undefined}>
          {isTicketDetailLayout && (
            <AttachmentPicker
              id="conv-attachments"
              files={pendingFiles}
              onChange={setPendingFiles}
              disabled={busy}
              iconOnly
            />
          )}
          <textarea
            className="form-control"
            rows={isTicketDetailLayout ? 1 : 3}
            placeholder={
              canReplyToParent && mode === 'parent'
                ? 'Message visible to the parent…'
                : 'Reply internally… use @ to tag someone'
            }
            value={text}
            onChange={(e) => handleComposerChange(e.target.value)}
            onBlur={emitTypingStop}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void handleSend();
              }
            }}
          />
          {mentionOptions.length > 0 && (
            <ul className="mention-menu" role="listbox">
              {mentionOptions.map((person) => (
                <li key={person.id}>
                  <button
                    type="button"
                    className="mention-menu-item"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      insertMention(person);
                    }}
                  >
                    {person.firstName} {person.lastName}
                    <span className="mention-menu-email">{person.email}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {isTicketDetailLayout && (
            <button
              type="submit"
              className="conv-wa-send"
              disabled={busy || !canSubmit}
              aria-label={canReplyToParent && mode === 'parent' ? 'Send message' : 'Add internal note'}
            >
              {busy ? '…' : '→'}
            </button>
          )}
        </div>
        <div className="conv-composer-actions">
          {!isTicketDetailLayout && (
            <AttachmentPicker
              id="conv-attachments"
              files={pendingFiles}
              onChange={setPendingFiles}
              disabled={busy}
            />
          )}
          {!isTicketDetailLayout && (
            <button
              type="submit"
              className={`btn ${canReplyToParent && mode === 'parent' ? 'btn-primary' : 'btn-internal-note'}`}
              disabled={busy || !canSubmit}
            >
              {busy ? 'Sending…' : canReplyToParent && mode === 'parent' ? 'Send →' : 'Add note'}
            </button>
          )}
        </div>
      </form>
  );

  if (isTicketDetailLayout) {
    return (
      <section className="conversation-thread conversation-thread--ticket-detail">
        {error && <div className="alert alert-error">{error}</div>}
        <div className="ticket-detail-scroll-body">
          {scrollPrefix}
          {messagesEl}
        </div>
        {composerEl}
      </section>
    );
  }

  return (
    <section className="conversation-thread">
      {error && <div className="alert alert-error">{error}</div>}
      {messagesEl}
      {composerEl}
    </section>
  );
}
